/**
 * EntityChangeCardShell - Shared wrapper for entity change cards.
 *
 * Provides the common outer frame, header button (chevron, icon, name, badges,
 * change type badge, acknowledge checkbox), and expanded content wrapper.
 * All 3 card types (Provider, TreatmentRequest, Coverage) use this shell.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { cn } from '@/lib/utils';
import type { ChangeType } from '@/services/api/generated/crossMatterChangesService';

import type { TransferChecklist } from '../utils/useTransferChecklist';
import {
  CHANGE_TYPE_BORDER,
  CHANGE_TYPE_CONFIG,
} from './changeTypeConfig';
import { TransferCheckbox } from './TransferCheckbox';

interface EntityChangeCardShellProps {
  changeType: ChangeType;
  name: string;
  description?: string | null;
  icon: React.ElementType;
  iconBgClassName: string;
  iconTextClassName: string;
  badges?: React.ReactNode;
  statusBadge?: React.ReactNode;
  changeCount: number;
  eventCount?: number;
  checklist: TransferChecklist;
  hasExpandedContent: boolean;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

export const EntityChangeCardShell: React.FC<EntityChangeCardShellProps> = ({
  changeType,
  name,
  description,
  icon: Icon,
  iconBgClassName,
  iconTextClassName,
  badges,
  statusBadge,
  changeCount,
  eventCount,
  checklist,
  hasExpandedContent,
  defaultExpanded,
  children,
}) => {
  const [expanded, setExpanded] = React.useState(
    defaultExpanded ?? changeType !== 'SAME',
  );

  const changeConfig = CHANGE_TYPE_CONFIG[changeType];
  const borderClass = CHANGE_TYPE_BORDER[changeType];
  const isUpdatedWithoutDiffs =
    changeType === 'UPDATED' && changeCount === 0;

  return (
    <div
      className={cn(
        'border border-gray-200 rounded-md overflow-hidden',
        borderClass && `border-l-4 ${borderClass}`,
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => hasExpandedContent && setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
          hasExpandedContent
            ? 'cursor-pointer hover:bg-gray-50/50'
            : 'cursor-default',
        )}
      >
        {!hasExpandedContent && <div className="h-4 w-4 shrink-0" />}
        {hasExpandedContent && expanded && (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        )}
        {hasExpandedContent && !expanded && (
          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
        )}

        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            iconBgClassName,
          )}
        >
          <Icon className={cn('h-4 w-4', iconTextClassName)} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-gray-900 truncate">
              {name}
            </span>
            {badges}
          </div>
          {description && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {statusBadge}
          <Badge color={changeConfig.color} className="text-xs">
            {changeConfig.label}
          </Badge>
          {changeCount > 0 && (
            <span className="text-xs text-gray-400">
              {changeCount} {changeCount === 1 ? 'change' : 'changes'}
            </span>
          )}
          {isUpdatedWithoutDiffs && !!eventCount && eventCount > 0 && (
            <span className="text-xs text-gray-400">
              {eventCount} {eventCount === 1 ? 'event' : 'events'}
            </span>
          )}
          {checklist.showAcknowledge && (
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <TransferCheckbox
                checked={checklist.acknowledgeChecked}
                onToggle={checklist.toggleAcknowledge}
              />
            </div>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && hasExpandedContent && (
        <div className="border-t border-gray-100 px-4 py-2.5 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};
